import { MassTortMatterSelectionModal } from "@/components/features/mass-torts/MassTortMatterSelectionModal";
import { useCreateMassTortSurveyDistribution } from "@/lib/data/react-query/mass-torts/mass-tort-survey";
import type { MassTortSurvey } from "@/lib/data/requests/mass-torts/mass-tort-survey";
import type { ReportsMatterSearchTableRow } from "@/lib/types";
import type { APIError } from "@/lib/types/APIResponse";
import { isAxiosError } from "axios";
import { useRef, useState } from "react";
import toast from "react-hot-toast";

function requestError(cause: unknown, fallback: string) {
  return isAxiosError<APIError>(cause)
    ? (cause.response?.data.meta.userMsg ?? fallback)
    : fallback;
}

export function DistributionPanel({
  survey,
  canDistribute,
  open,
  onOpenChange,
}: {
  survey: MassTortSurvey;
  canDistribute: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [selectedMatters, setSelectedMatters] = useState<
    ReportsMatterSearchTableRow[]
  >([]);
  const pendingRequest = useRef<{ signature: string; key: string } | null>(
    null,
  );
  const create = useCreateMassTortSurveyDistribution();

  const distribute = async () => {
    if (!open || !canDistribute) return;
    const selectionMode = "subset";
    const matterIds = [
      ...new Set(selectedMatters.map((matter) => matter.matterId)),
    ].sort();
    if (!matterIds.length) return;
    const signature = JSON.stringify({
      surveyId: survey.id,
      dateModified: survey.dateModified,
      selectionMode,
      matterIds,
    });
    if (pendingRequest.current?.signature !== signature) {
      pendingRequest.current = { signature, key: crypto.randomUUID() };
    }
    try {
      await create.mutateAsync({
        surveyId: survey.id,
        dateModified: survey.dateModified,
        requestKey: pendingRequest.current.key,
        selectionMode,
        matterIds,
      });
      pendingRequest.current = null;
      onOpenChange(false);
      toast.success(
        "Survey distribution started. Matter surveys are being created.",
      );
    } catch (cause) {
      toast.error(
        requestError(cause, "Could not create the matter surveys. Try again."),
      );
    }
  };

  return (
    <MassTortMatterSelectionModal
      open={open}
      setOpen={onOpenChange}
      massTortId={survey.massTortId}
      selectedMatters={selectedMatters}
      setSelectedMatters={setSelectedMatters}
      purpose="survey-distribution"
      defaultSelectAll
      confirmDisabled={!canDistribute}
      onConfirm={() => void distribute()}
      confirmLabel={`Distribute surveys (${selectedMatters.length})`}
      confirmPending={create.isPending}
      confirmError={
        !canDistribute
          ? "Add at least one question before distributing this survey."
          : ""
      }
    />
  );
}
